import type { EditorMessages } from '../de/editor.js';

export const editor: EditorMessages = {
  common: {
    byMeasure: 'Create with dimensions',
    invalidDimensions: 'Please enter valid dimensions.',
    orthogonal: '90° angles only',
    paste: 'Paste',
    discard: 'Discard',
    duplicate: 'Duplicate',
    show: 'Show',
    hidden: 'hidden',
    outsideHalls: 'outside halls',
    deleteAll: 'Delete all',
  },
  fields: {
    stepUp: 'Increase {label} by 5 cm',
    stepDown: 'Decrease {label} by 5 cm',
    stepUpShort: '5 cm more',
    stepDownShort: '5 cm less',
    width: 'Width',
    length: 'Length',
    depth: 'Depth',
    height: 'Height',
    eaveHeight: 'Eave height',
    roomHeight: 'Room height',
    wallThickness: 'Wall thickness',
    roofType: 'Roof type',
    kind: 'Type',
    type: 'Type',
    category: 'Category',
    count: 'Quantity',
    form: 'Shape',
    label: 'Label',
    color: 'Colour',
    area: 'Area',
    note: 'Note',
    location: 'Location',
    level: 'Floor',
    centerX: 'Centre X',
    centerY: 'Centre Y',
    positionX: 'Position X',
    positionY: 'Position Y',
    startX: 'Start X',
    startY: 'Start Y',
    endX: 'End X',
    endY: 'End Y',
  },
  groups: {
    dimensions: 'Dimensions',
    position: 'Position & rotation',
    metrics: 'Key figures',
    roof: 'Roof',
    facade: 'Facade',
    openings: 'Gates, doors, windows',
    roofElements: 'Roof elements',
  },
  nouns: {
    columns: 'Column',
    sites: 'Site',
    buildings: 'Hall',
    office: 'Office building',
    walls: 'Wall',
    openings: 'Opening',
    zones: 'Zone',
    rooms: 'Room',
    objects: 'Object',
    measurements: 'Dimension',
    roofElements: 'Roof element',
    stairs: 'Stairs',
    roads: 'Road',
    parkings: 'Car park',
    fences: 'Fence',
    gates: 'Sliding gate',
    mezzanines: 'Mezzanine',
    canopies: 'Canopy',
    wallImages: 'Wall picture',
    railings: 'Railing',
    element: 'Element',
  },
  counts: {
    sites: { one: '{n} site', other: '{n} sites' },
    buildings: { one: '{n} hall', other: '{n} halls' },
    walls: { one: '{n} wall', other: '{n} walls' },
    openings: { one: '{n} opening', other: '{n} openings' },
    zones: { one: '{n} zone', other: '{n} zones' },
    rooms: { one: '{n} room', other: '{n} rooms' },
    objects: { one: '{n} object', other: '{n} objects' },
    measurements: { one: '{n} dimension', other: '{n} dimensions' },
    roofElements: { one: '{n} roof element', other: '{n} roof elements' },
    stairs: { one: '{n} staircase', other: '{n} staircases' },
    roads: { one: '{n} road', other: '{n} roads' },
    parkings: { one: '{n} car park', other: '{n} car parks' },
    fences: { one: '{n} fence', other: '{n} fences' },
    gates: { one: '{n} sliding gate', other: '{n} sliding gates' },
    mezzanines: { one: '{n} mezzanine', other: '{n} mezzanines' },
    canopies: { one: '{n} canopy', other: '{n} canopies' },
    wallImages: { one: '{n} wall picture', other: '{n} wall pictures' },
    railings: { one: '{n} railing', other: '{n} railings' },
    offices: { one: '{n} office building', other: '{n} office buildings' },
    stalls: { one: '{n} parking space', other: '{n} parking spaces' },
    elements: { one: '{n} element', other: '{n} elements' },
  },
  remove: {
    sites: 'Delete site',
    buildings: 'Delete hall',
    office: 'Delete office building',
    walls: 'Delete wall',
    openings: 'Delete opening',
    closeVoid: 'Close the wall again',
    zones: 'Delete zone',
    rooms: 'Delete room',
    objects: 'Delete object',
    measurements: 'Delete dimension',
    roofElements: 'Delete roof element',
    stairs: 'Delete stairs',
    roads: 'Delete road',
    parkings: 'Delete car park',
    fences: 'Delete fence',
    gates: 'Delete sliding gate',
    mezzanines: 'Delete mezzanine',
    canopies: 'Delete canopy',
    wallImages: 'Delete picture',
    railings: 'Delete railing',
    element: 'Delete element',
    many: { one: 'Delete {n} element', other: 'Delete {n} elements' },
  },
  toolPanel: {
    aria: 'Tools',
    modes: 'Tool mode',
  },
  tools: {
    select: 'Select',
    draw: 'Draw',
    move: 'Move',
    moveShort: 'Move',
    rotate: 'Rotate',
    measure: 'Measure',
  },
  steps: {
    site: '1 · Site',
    hall: '2 · Hall',
    office: '3 · Office buildings & stairs',
    interior: '4 · Interior layout',
    openings: '5 · Gates, doors, windows',
    objects: '6 · Objects',
    outdoor: '7 · Outdoor areas',
    placed: 'Placed objects',
  },
  summary: {
    noSite: 'none yet',
    noHall: 'none yet',
    parcels: { one: '{n} plot · {area}', other: '{n} plots · {area}' },
    halls: { one: '{n} hall · {area}', other: '{n} halls · {area}' },
    hiddenObjects: '{n} · {hidden} hidden',
  },
  site: {
    draw: '✎ Draw site in the plan',
    byMeasureInfo:
      'Rectangle, trapezoid or L-shape with fixed dimensions. Afterwards, “Edit shape” lets you drag its corners and edges.',
    parcels: 'Plots ({n})',
    total: 'Total',
    addFree: 'Another site with dimensions (placed freely)',
    addInfo:
      'Appears to the right of the existing sites. Then select it and drag it to its neighbours – it snaps to corners and edges.',
    replaceAll: 'Start over (replaces all sites)',
    shapes: { rect: 'Rectangle', trapez: 'Trapezoid', l: 'L-shape' },
    invalidSize: 'Please enter a valid width and length.',
    cutTooLarge: 'The cut-out must be smaller than the site.',
    invalidTop: 'Please enter a valid top width.',
    confirmReplace: 'Replace all existing sites?',
    formAria: 'Create site',
    shapeAria: 'Site shape',
    widthBottom: 'Bottom width',
    widthTop: 'Top width',
    shift: 'Top offset (empty = centred)',
    cutWidth: 'Cut-out width',
    cutLength: 'Cut-out length',
    replace: 'Replace site',
    add: 'Add site',
    create: 'Create site',
  },
  sitePanel: {
    sides: { north: 'North', east: 'East', south: 'South', west: 'West' },
    attachTitle: 'Attach to “{name}”',
    attachInfoEast:
      'Rectangular neighbouring site flush against one side. Offset towards the east, negative in the opposite direction. Afterwards it can be moved freely and snaps to neighbouring edges.',
    attachInfoNorth:
      'Rectangular neighbouring site flush against one side. Offset towards the north, negative in the opposite direction. Afterwards it can be moved freely and snaps to neighbouring edges.',
    attachAria: 'Attach site',
    side: 'Side',
    alongLength: 'Length along the edge',
    offsetAlong: 'Offset along the edge',
    attach: '＋ Attach site',
    straighten: 'Straighten',
    straightenBy: 'Straighten ({deg}°)',
    straightenTitle: 'Rotates so that the longest site edge is horizontal or vertical',
    selectAll: 'Select all sites',
    rotateBackground: 'Rotate background image too',
    moveBackground: 'Move background image too',
    nameLabel: 'Label (e.g. plot number)',
    perimeter: 'Perimeter',
    extent: 'Overall dimensions',
    allSites: 'All {n} sites',
    rectDims: 'Dimensions (rectangle)',
    shapeInfo:
      'Drag corners freely, e.g. into a trapezoid. The arrows on the edges move the whole edge in parallel. Right-clicking an edge inserts a corner there.',
    shapeStop: 'Finish editing shape',
    shapeStart: 'Edit shape (drag corners and edges)',
    vertices: 'Corners and edges as numbers',
    positionInfo:
      'Drag the site in the plan to move it – it snaps to neighbouring sites. Straighten turns the longest edge horizontal or vertical.',
  },
  interior: {
    byMeasureInfo:
      'Appears in the middle of the selected hall and can be moved afterwards. Draw freely shaped zones and rooms with the “Draw” tool.',
    formAria: 'Create zone or room',
    createZone: 'Create zone',
    createRoom: 'Create room',
  },
  draw: {
    title: 'Draw',
    what: 'What to draw',
    kinds: {
      site: 'Site',
      zone: 'Zone',
      room: 'Room',
      wall: 'Wall',
      road: 'Road',
      parking: 'Car park',
      fence: 'Fence',
      hall: 'Hall',
      office: 'Office building',
      storey: 'Floor outline',
      mezzanine: 'Mezzanine',
      railing: 'Railing',
      column: 'Column',
    },
    hints: {
      column:
        'Click the spot in the hall or office storey where the column should stand. Its height follows automatically (up to the mezzanine, the slab or the eaves).',
      storey:
        'Click the corners of the smaller upper floor in the plan, ideally on the building corners. Finish by clicking the first point, double-clicking or pressing Enter.',
      office:
        'Click the corners of the office building one after another (e.g. on the site plan). Finish by clicking the first point, double-clicking or pressing Enter. Floors and heights come from the form; an outline inside a hall becomes a built-in office.',
      railing:
        'Click the points one after another, e.g. along a ramp or gallery. Finish by double-clicking, pressing Enter or the button. The railing stands on the currently selected level.',
      mezzanine:
        "Click the corners of the walkable mezzanine inside a hall. Finish by clicking the first point, double-clicking or pressing Enter. It becomes the hall's 1st floor.",
      hall: 'Click the corners of the hall one after another (e.g. on the site plan). Finish by clicking the first point, double-clicking or pressing Enter. For flat and mono-pitch roofs; corners can be moved, inserted and deleted afterwards.',
      wall: 'Drag from point to point in the plan. Corners and walls are snapped.',
      road: 'Click the points one after another (e.g. on the site plan). Finish by double-clicking, pressing Enter or the button. Starting at the end of a road extends it.',
      fence:
        'Click the points one after another. Finish by double-clicking, pressing Enter or the button. Starting at the end of a fence extends it.',
      parking:
        'Click the start and end of the parking row, then its depth – also at an angle to the grid. Or quickly drag a rectangle.',
      zone: 'Click the corners one after another, finish by clicking the first point, double-clicking or pressing Enter. Or quickly drag a rectangle.',
      room: 'Drag a rectangle in the plan, then adjust the dimensions on the right.',
      site: 'Click the corners one after another (e.g. on the site plan). Finish by clicking the first point, double-clicking or pressing Enter. Points snap to neighbouring sites.',
    },
    wallRules: {
      title: 'Wall rules',
      items: [
        'At the facade: the wall ends at the inner face of the exterior wall, even if you drag up to 50 cm beyond it. Gaps up to 15 cm are closed.',
        'At other walls: up to 25 cm overshoot is set back flush, gaps up to 15 cm are closed. Larger overshoots and passages stay.',
        'Moving (mouse, S, arrow keys): walls in a T-joint or an inexact corner are lengthened or shortened and stay attached (up to 5 cm counts as connected). Exact corners move as a corner.',
        'Locked walls and the exterior walls of buildings are never adjusted.',
        'Keys: S move, E resize, Alt while dragging turns snapping off.',
      ],
    },
    zoneCategory: 'Zone category',
    roomType: 'Room type',
    wallType: 'Wall type',
    roadKind: 'Road type',
    parkingKind: 'Parking space type',
    firstPoint: 'Set the first corner in the plan',
    points: { one: '{n} corner set', other: '{n} corners set' },
    removeLast: 'Remove last',
    finishSite: 'Finish site',
    finishZone: 'Finish area',
    finishRoad: 'Finish road',
    finishFence: 'Finish fence',
    finishHall: 'Finish hall',
    finishOffice: 'Finish office building',
    finishStorey: 'Finish floor outline',
    finishMezzanine: 'Finish mezzanine',
    finishRailing: 'Finish railing',
    firstLinePoint: 'Set the start point in the plan',
    linePoints: { one: '{n} point set', other: '{n} points set' },
    rowSteps: [
      'Set the start of the parking row',
      'Set the end of the row',
      'Set the depth (side of the spaces)',
    ],
  },
  mezzanine: {
    areas: 'Areas',
    areasInfo:
      'The walls on the mezzanine divide it into areas (wall thickness and linings taken off, doors separate). A name replaces “Area n” in the plan, the area overview and the PDF; leave it empty for the number.',
    areaRow: 'Area {n} · {area}',
    defaultName: 'Mezzanine {n}',
    draw: 'Draw mezzanine',
    drawInfo:
      "Walkable partial floor on the hall's 1st floor with slab, railing, columns and stairs.",
    elevation: 'Floor level',
    slab: 'Slab thickness',
    railing: 'Railing on free edges',
    columns: 'Columns',
    color: 'Colour',
    area: 'Area',
    headroom: 'Clear height below',
    above: 'Clear height above (to eaves)',
    addStair: 'Add stairs',
    stairs: { one: '{n} staircase', other: '{n} staircases' },
    showLevel: 'Show 1st floor',
    toHall: 'To hall {name}',
    info: 'Drag corners in the 2D plan; right-click an edge to insert a corner. Zones, rooms and objects can be planned on the 1st floor.',
  },
  wallImage: {
    section: 'Pictures & logos',
    sectionInfo:
      'Put a company lettering, logo or picture on this wall: PNG (transparency is kept) or JPEG. It hangs just in front of the wall, moves with it, and is moved in 3D with the arrows and scaled at its corners with “Resize” (E).',
    add: 'Add picture …',
    adding: 'Uploading …',
    none: 'No pictures on this wall yet.',
    failed: 'The picture could not be uploaded.',
    info: 'Position and size on the wall; transparent parts of a PNG show the wall behind.',
    offset: 'Distance from wall start',
    bottom: 'Lower edge above floor',
    width: 'Width',
    height: 'Height',
    keepAspect: 'Keep proportions',
    flip: 'To the other side of the wall',
    center: 'Centre on the wall',
    toWall: 'To the wall',
    pixels: '{width} × {height} px',
    copy: 'Copy (Ctrl+C)',
    copied: 'Copied – pick the target wall and press Ctrl+V',
    paste: { one: 'Paste copied picture', other: 'Paste {n} copied pictures' },
    used: 'Used in this project',
    usedHint: 'Tap to put it on this wall in the size set last.',
  },
  canopy: {
    title: 'Canopy',
    overOpening: 'Canopy over this opening',
    overGates: 'Canopy over all gates of this wall',
    facade: 'Facade',
    facadeInfo:
      'Open the whole wall, e.g. where two halls are built together. Parts of a facade are opened with the opening type "Open wall section"; the wall above stays.',
    openWall: 'Open whole wall',
    addToWall: 'Canopy on this wall',
    depth: 'Projection',
    height: 'Underside height',
    pitch: 'Pitch',
    posts: 'Posts under the front edge',
    flip: 'To the other side of the wall',
    info: 'Hangs on the wall and moves with it. Drag it along the wall in the plan; the handles change width and projection.',
  },
  railing: {
    update: 'Change railing',
    info: 'When drawing, what the railing stands on is detected: mezzanine, office floor or office roof; otherwise the ground (e.g. at ramps). Choose flat hall roofs and own heights here under "Mounting".',
    mount: 'Mounting',
    auto: 'Detect automatically',
    ground: 'Ground floor / site',
    roofOf: 'Roof of {name}',
    custom: 'Own height',
    elevation: 'Height of the surface',
  },
  ral: {
    label: 'RAL colour',
    custom: 'Custom colour',
    common: 'Typical corporate colours',
  },
  hall: {
    facadeKind: 'Cladding',
    facadeKinds: {
      'iso-vertical': 'Sandwich panels, vertical',
      'iso-horizontal': 'Sandwich panels, horizontal',
      trapezoid: 'Trapezoidal sheet',
      smooth: 'Smooth (render)',
      brick: 'Clinker brick',
    },
    brickColors: { red: 'Red clinker', brown: 'Brown clinker', yellow: 'Yellow clinker' },
    facadeColor: 'Facade colour',
    trimColor: 'Trims and roof edge',
    gateColor: 'Gate colour',
    roofColor: 'Roof colour',
    storeyColor: 'Facade {level}',
    likeBuilding: 'Like building',
    standardRoof: 'Default',
    floorKind: 'Hall floor',
    floorKinds: {
      concrete: 'Concrete (power-floated)',
      coated: 'Coated industrial floor',
      plain: 'Plain colour',
    },
    facadeInfo:
      'Shown in 3D, in screenshots and in the 3D views of the PDF. Sandwich panels on a 1.00 m grid; the trim colour applies to corner profiles, roof edge, gutter and the frames of gates, doors and windows.',
    create: 'Create hall',
    invalid: 'Please enter valid hall dimensions.',
    roofTypes: { gable: 'Gable roof', mono: 'Mono-pitch roof', flat: 'Flat roof' },
    pitch: 'Roof pitch',
    areaLine: 'Built-up area {built} · usable area inside {net}',
    areaLineOffice: 'Built-up area {built}',
    areaLineFloors: 'Built-up area {built} · gross floor area {gross} ({n} storeys)',
    pitchInfo: 'equals a {percent} % slope · allowed {min}–{max}°',
    drawInPlan: 'Draw hall in the plan',
    drawRoofOnly: 'Drawn halls have a flat or mono-pitch roof.',
    eaveEdge: 'Eave edge (low side)',
    edgeOption: 'Edge {n} · {length} · {dir}',
    directions: ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE'],
    outlineInfo:
      'Drawn outline with {n} corners. Drag corners in the 2D plan; right-click an edge to insert a corner, right-click a corner to delete it.',
    grossArea: 'Hall area (external)',
    netArea: 'Usable floor area',
    ridgeHeight: 'Ridge height',
    openingsInfo: 'Choose a wall to add openings individually or as a row.',
    roofElementsInfo:
      'Skylight domes, rooflight strips and smoke vents, individually or as a grid. Tap placed elements in the floor plan to move or change them.',
  },
  structure: {
    title: 'Structure',
    info: 'Haunched steel frames of columns and rafters (I sections), evenly spread at the bay spacing; the columns stand inside along the facade, behind the wall girts with vertical panels. For halls wider than 30 m a row of interior columns is added, by default under every second rafter; the rafters between rest on a beam. Bays over 5 m make the frames heavier automatically; from 7.5 m rolled sections replace the cold-formed purlins and girts. The profiles are a rough pre-sizing for orientation (snow load from the project settings under “Loads”) and do not replace a structural analysis.',
    enabled: 'Work out and show the structure',
    system: 'Construction',
    systems: {
      steel: 'Steel frames',
      hybrid: 'Concrete columns with steel binders',
      precast: 'Precast concrete (columns and prestressed binders)',
    },
    systemsInfo:
      'Concrete columns stand fixed in socket footings (cross section about height / 18, at least 40 cm) and brace the walls. Binders 7.50 m apart by default; interior columns only beyond 30 m (steel binders) or 40 m span (prestressed). Steel binders rest on them pinned; prestressed binders follow the roof pitch on top, are at most about span / 15 deep and under a steeper roof their underside rises too.',
    concreteColumn: 'Column cross section (concrete)',
    girderProfile: 'Binder profile (simply supported)',
    girder: 'Binder depth (support / deepest)',
    girderValue: '{support} / {deepest}',
    roofBracing: 'Roof bracing and struts',
    bay: 'Bay spacing (max.)',
    interiorRows: 'Interior columns',
    rows: ['None', '1 row', '2 rows', '3 rows'],
    interiorSpacing: 'Interior columns at',
    spacings: ['Every axis', 'Every 2nd axis', 'Every 3rd axis'],
    beamProfile: 'Beam (interior row)',
    auto: '{value} (auto)',
    columnProfile: 'Column profile',
    rafterProfile: 'Rafter profile',
    gableColumns: 'Gable wall columns',
    purlins: 'Purlins (roof)',
    girts: 'Wall girts (for vertical panels)',
    girtsOptional: 'Wall girts (not needed for this facade)',
    bracing: 'Bracing and eave struts',
    haunches: 'Haunches at the frame corners',
    conflict: '{opening} hits a column on axis {axis}',
    conflictGable: '{opening} hits a gable wall column',
    grid: 'Grid',
    gridValue: '{n} bays × {length}',
    rafterSpan: 'Rafter span',
    roofLoad: 'Roof load (snow + own weight)',
    roofLoadValue: '{value} kN/m²',
    windLoad: 'Wind pressure qₚ (ridge height)',
    clearWidth: 'Clear width between columns',
    clearLength: 'Clear length',
    outline: 'The structure is not shown yet for halls with a drawn outline.',
  },
  pv: {
    label: 'Rooftop PV system (+{load} kN/m²)',
    share: 'Covered roof area',
    info: 'Modules with their mounting as an extra permanent roof load (about 20 kg/m²) for binders, purlins, columns and foundations; in the estimate as kWp from the covered roof area (about 0.2 kWp per m²).',
  },
  officeStructure: {
    title: 'Structure',
    info: 'Load-bearing walls: the slabs rest on the exterior walls and massive inner walls; it is checked whether the slab manages the span. Reinforced concrete skeleton: columns in a grid (facade divided evenly), sized from the floor area each column carries and all storeys above (office 3 kN/m², usable top slab 5 kN/m², roof with snow); the slabs as flat slabs or on beams. Enclosed stairwells count as concrete cores. A pre-sizing for orientation, not a structural analysis.',
    system: 'Structural system',
    systems: {
      walls: 'Load-bearing walls',
      flat: 'Concrete skeleton with flat slabs',
      beams: 'Concrete skeleton with beams',
    },
    grid: 'Column grid (max.)',
    columnSize: 'Column cross section',
    auto: '{value} (auto)',
    fields: 'Grid',
    fieldsValue: '{nx} × {ny} fields, {x} × {y}',
    columnsOf: 'Columns {level}',
    columnsValue: '{n} pcs, {size} × {size}',
    beam: 'Beams (w × h)',
    beamValue: '{width} × {height}',
    span: 'Slab span',
    slab: 'Slab thickness',
    slabValue: '{current} (recommended {recommended})',
    adopt: 'Use the recommended slab thickness {value}',
  },
  costs: {
    title: 'Cost estimate',
    intro:
      'Plan quantities times this project’s unit prices. Prices and surcharges can be changed right in the table.',
    rangeLabel: 'Budget range:',
    steelExtra: 'Allowance for connections (steel)',
    range: 'Range ±',
    morePrices: 'More unit prices',
    resetPrices: 'Remove all own prices (use the catalog prices)',
    catalog: 'Price catalog',
    catalogDefault: 'Default ({name})',
    ownPriceReset: 'Own price – back to the catalog price',
    foundationsFromColumns: 'Work out foundations from columns and walls',
    foundationsFromColumnsHint:
      'Pad footings from the load of each column (ground pressure 250 kN/m², 80 cm deep and frost-free along the facade), strip footings and frost aprons under the walls; replaces the percent surcharge for foundations.',
    foundationsMeasured: 'worked out',
  },
  hallSections: {
    title: 'Areas formed by partition walls',
    info: 'Determined automatically from the partition walls, measured to the wall centre line. Gaps of up to 1 m to the next wall count as closed (dashed in the plan).',
    adoptedAs: 'Adopted as',
    hasHoles: 'Contains an office or a room – please draw the zone by hand.',
    zoneKindFor: 'Zone type for {name}',
    adopt: 'Adopt as zone',
  },
  roofElements: {
    daylight: 'Roof daylight ratio',
    smokeVentArea: 'Smoke vent area (geometric)',
    typeAria: 'Roof element',
    arrangement: 'Arrangement',
    grid: 'Grid',
    single: 'Single',
    gableAria: 'Position on the gable roof',
    halves: 'Per roof half',
    ridge: 'On the ridge',
    roof: 'Whole roof',
    widthAcross: 'Width (across)',
    lengthAlong: 'Length (along)',
    spacingU: 'Spacing across',
    spacingV: 'Spacing along',
    marginHalves: 'Distance to eave/ridge/gable',
    margin: 'Edge distance',
    ridgeHint: '“across” = across the ridge, “along” = along the ridge.',
    halvesHint: 'Symmetrical to the ridge, centred on both roof surfaces.',
    eaveHint: '“across” = across the eave, “along” = along the eave.',
    outlineHint: 'Only elements lying completely on the roof, edge distance included, are placed.',
    placeSingle: 'Place {type} in the middle of the hall',
    placeSingleRoof: 'Place {type} on the roof',
    placeGrid: 'Place {n} × {type}',
    noFit: 'Does not fit on the roof with these distances',
    removeAll: 'Remove all roof elements of this hall',
  },
  office: {
    title: 'Office building',
    infoIndoor:
      'Inside the hall: stands under the hall roof with a concrete slab on top, usable as floor space (e.g. storage mezzanine). Floors and heights can be adjusted per floor afterwards.',
    infoOutdoor:
      'Free-standing with a flat roof, e.g. in front of the hall. Floors and heights can be adjusted per floor afterwards.',
    formAria: 'Create office building',
    placementAria: 'Location of the office building',
    indoor: 'Inside the hall',
    outdoor: 'Free-standing',
    floors: 'Floors',
    clearHeight: 'Clear height per floor',
    slab: 'Slab incl. floor finish',
    invalid: 'Please enter valid dimensions (at least 2 m, clear height at least 2 m).',
    create: 'Create office building',
    drawOutline: 'Draw outline (e.g. trapezoid)',
  },
  officePanel: {
    areasInfo:
      'Walls you draw divide the storey shown into areas (wall thicknesses and linings taken off, doors separate, rooms laid out count on their own). A name replaces “Area n” in the plan and the area overview; ⌖ zooms there.',
    enterMode: 'Edit office building (office mode)',
    placementIndoor: 'Inside the hall (built-in)',
    placementOutdoor: 'Outside (free-standing, flat roof)',
    dimsFloors: 'Dimensions & floors',
    floorCount: 'Number of floors',
    clearHeightOf: 'Clear height {level}',
    usableTop: 'Use the top as a gallery (railing, reachable by stairs)',
    deckRailing: 'Gallery railing',
    sideFront: 'Front',
    sideRight: 'Right',
    sideBack: 'Back',
    sideLeft: 'Left',
    sideLength: '{side} ({length})',
    railingAll: 'All',
    railingLong: 'Long sides only',
    railingNone: 'None',
    railingAutoHint:
      'Along a hall wall or an adjoining mezzanine the railing is left out automatically.',
    deckStair: 'Stairs up to the gallery',
    storeys: 'Floors (setback storey)',
    storeysInfo:
      'An upper floor can be smaller than the one below: trace its outline in the plan on that floor. The exterior walls follow the new outline; the free area next to it becomes a flat roof with parapet or a roof terrace with railing. Floors above take over the outline.',
    storeyInherited: '{level}: as below',
    storeyOwn: '{level}: own outline · {area}',
    storeyDraw: 'Draw outline',
    storeyReset: 'As below',
    storeyFree: 'Free area next to it',
    storeyFreeRoof: 'Flat roof with parapet',
    storeyFreeTerrace: 'Roof terrace with railing',
    removedPieces: 'Removed exterior walls',
    removedPiecesInfo:
      'Clicking an exterior wall in 3D or the plan and deleting it removes it on that floor only. Upper floors get a railing there if wanted.',
    removedPiece: '{level} · wall {n}',
    restorePiece: 'Restore',
    showDeck: 'Show and edit the gallery',
    restoreWalls: 'Restore exterior walls',
    extendRooms: 'Fit rooms to the slab edge',
    removedWalls: 'Removed exterior walls: {n}. They stay away when moving or resizing.',
    totalHeight: 'Total height',
    floorOf: 'Floor level {level}',
    airTo: 'Clearance to eave {name}',
    footprint: 'Footprint',
    gfa: 'Gross floor area, all floors',
    stairs: 'Stairs',
    noStairs: 'No stairs yet – add them on the left under “3 · Office buildings & stairs”.',
    walls: 'External walls · doors, windows',
    upperWindows:
      'Upper-floor windows: select the opening and set the storey in the “Floor” field.',
    unassigned: 'Unassigned · {area} ({w} × {l} m)',
    restRoomType: 'Room type for the remaining area',
    adoptRoom: 'Adopt as room',
    roomBook: 'Rooms · room schedule',
    noRooms: 'No rooms yet. Drag rooms in the plan with “Draw → Room” or use a template below.',
    unassignedOf: 'of which unassigned {area}',
    templateFor: 'Room layout from template for {level}',
    template: 'Template',
    templateAria: 'Room template',
    templateWallAria: 'Wall type of the template',
    templateHint: 'The floor is chosen at the top right of the plan.',
    applyTemplate: 'Apply template to {level}',
  },
  stair: {
    title: 'Stairs',
    info: 'Inside, the stairs sit in the north-east corner of the office building; outside, they are attached to the east wall. Can be moved and rotated freely afterwards.',
    insert: 'Insert stairs',
    building: 'Building',
    buildingAria: 'Building of the stairs',
    needsFloor: 'Stairs require a second floor or a usable slab.',
    formAria: 'Stair type',
    upTo: 'Up to floor',
    from: 'From floor',
    stairwellHint:
      'One stair per floor on top of each other; moving, changing and deleting apply to all.',
    fromAria: 'Stairs from floor',
    width: 'Flight width',
    outside: 'Outside the building (attached)',
    podest: 'Depth of the exit landing',
    landingOn: 'Exit landing at the top',
    construction: 'Construction',
    constructionSolid: 'Solid (closed body)',
    constructionSteel: 'Steel stair (open, free underneath)',
    constructionSlab: 'Concrete stair (waist slab, free underneath)',
    goingOwn: 'Going (cm)',
    riserCount: 'Risers',
    standard: 'standard',
    entry: 'Landing in front of the first step',
    restoreStairwell: 'Restore stairwell walls',
    protrudes:
      'The stairwell reaches past the building. Reduce going, risers or the entry landing, or move the stair.',
    withDoor: 'Add a door from the landing to the upper floor',
    enclosed: 'Stairwell with walls',
    midLanding: 'Intermediate landing (flight split in the middle, 1 m)',
    sideExit: 'Side exit',
    sideExitNone: 'None (straight on)',
    sideExitLeft: 'To the left',
    sideExitRight: 'To the right',
    sideExitLength: 'Platform length',
    sideExitToWall: 'Extend to the wall',
    sideExitNoWall: 'There is no wall at platform height within 20 m in this direction.',
    sideExitHint:
      'Platform at the top as wide as the stair, e.g. across the columns and girts of the hall wall to the door into the office.',
    snapping: 'Snap and dock',
    snappingHint:
      'Off: the stair moves freely, without grid and without jumping onto walls or columns. On a computer, holding Alt does the same.',
    startOn: 'Starts on',
    leadsTo: 'Leads to',
    roofOf: 'Roof of {name}',
    hallFloor: 'Hall floor (ground floor)',
    startHeight: 'Starting height',
    ownHeight: 'Own height',
    wallSides: 'Walls of the stairwell',
    sides: { front: 'Start (with door)', back: 'Back (landing)', left: 'Left', right: 'Right' },
    risers: 'Risers',
    going: 'Going',
    footprint: 'Footprint',
    turn: 'Turn',
    turnRight: 'to the right',
    turnLeft: 'to the left',
    addDoor: 'Add door at the landing',
    floorHeight: 'Floor-to-floor height',
    ownRise: 'Stair height (own)',
    autoRise: 'Height automatic again',
    stepRule: 'Step rule (2 r + g)',
    stairFootprint: 'Stair footprint',
    asrOk:
      'Meets the guide values of the German workplace rule ASR A1.8 (planning note, not a building code check).',
    toBuilding: 'Go to building ({name})',
  },
  fence: {
    kind: 'Fence type',
    color: 'Colour',
    colors: {
      ral6005: 'Moss green (RAL 6005)',
      ral7016: 'Anthracite (RAL 7016)',
      galvanized: 'Galvanized',
    },
    customColor: 'Custom colour',
    addGate: 'Insert sliding gate',
    addGateHere: 'Insert sliding gate into this fence',
    gateInfo:
      'Electric cantilever sliding gate. Move it freely; near a fence it snaps onto the fence line and leaves a gap in the fence there.',
    gateWidth: 'Gate width',
    opensTo: 'Opens to the',
    left: 'left',
    right: 'right',
    addDoor: 'Insert pedestrian door',
    addDoorHere: 'Insert pedestrian door into this fence',
    doorTitle: 'Pedestrian door',
    removeDoor: 'Delete pedestrian door',
    doorInfo:
      'Single-leaf door for pedestrians. Move it freely; near a fence it snaps onto the fence line and leaves a gap in the fence there. Turning it by 180° swaps the opening direction.',
    doorWidth: 'Clear width',
    hinge: 'Hinged on the',
    gateKind: 'Type',
    kinds: { sliding: 'Sliding gate', door: 'Pedestrian door' },
  },
  column: {
    title: 'Column',
    place: 'Place column (steel or concrete)',
    material: 'Type',
    materials: { steel: 'Steel column (HEB)', concrete: 'Concrete column' },
    height: 'Height',
    auto: 'automatic',
    autoHeight: 'Automatic height ({height})',
    standsOn: 'Stands in',
    hint: 'Without a height of its own a column reaches up to the mezzanine above or the eaves in a hall, and up to the slab of its storey in an office. An own height counts from the floor it stands on.',
    remove: 'Delete column',
    toBuilding: 'To “{name}”',
    mezzanineColumns: 'Columns',
    detach: 'Edit columns one by one',
    detachHint:
      'Turns the automatic supports into single steel columns that can be deleted, moved and changed.',
    restore: 'Restore automatic columns',
    detached: '{n} single columns',
  },
  lining: {
    title: 'Drywall lining',
    info: 'Plasterboard lining in front of the wall, e.g. in front of the bare hall wall in the office area on the mezzanine: from the floor of the chosen level up to the ceiling, with sills at the windows; doors and gates stay open. It reduces the room areas.',
    add: 'Add drywall lining',
    sideRight: 'Right face',
    sideLeft: 'Left face',
    level: 'Level',
    from: 'From (from wall start)',
    to: 'To (from wall start)',
    thickness: 'Thickness',
    span: 'Height',
    spanValue: '+{base} to +{top}',
    color: 'Colour',
    remove: 'Remove drywall lining',
  },
  handover: {
    title: 'Handover gates',
    info: 'Sliding mesh gate in the railing, e.g. for a forklift putting pallets onto the mezzanine. The railing ends at both sides of the gate; when walking it slides aside but still guards the edge.',
    add: 'Insert handover gate',
    edge: 'Edge',
    edgeOption: 'Edge {n} ({length})',
    offset: 'Distance from the edge start',
    width: 'Width',
    remove: 'Remove handover gate',
  },
  wall: {
    interior: 'Interior wall',
    exterior: 'Exterior wall',
    throughMezzanine: 'Down to the hall floor',
    throughMezzanineHint:
      'Off: the wall stands on the mezzanine. On: it reaches down to the hall floor; its top stays.',
    cladding: 'Surface',
    claddings: {
      plain: 'Plain / plastered',
      sandwich: 'Sandwich panel vertical',
      'sandwich-horizontal': 'Sandwich panel horizontal',
      'sand-lime': 'Sand-lime brick',
      concrete: 'Fair-faced concrete',
      brick: 'Clinker',
    },
    wallType: 'Wall type',
    ownWallType: 'Own (thickness/surface changed)',
    direction: 'Direction',
    ownerHall: 'This wall is controlled by the dimensions of hall “{name}”.',
    removeExterior: 'Remove exterior wall',
    floorRailing: 'Railing',
    removeOnFloor: 'Remove on {level} only',
    ownerRoom: 'This wall is controlled by the dimensions of room “{name}”.',
    toHall: 'Go to hall “{name}”',
    toRoom: 'Go to room “{name}”',
    ownerStair: 'This wall belongs to the stairwell “{name}” and moves with the stair.',
    toStair: 'To stair “{name}”',
    removeStairWall: 'Remove stairwell wall',
    openingsInfo:
      'An opening lands in the middle of the wall or next to existing ones. Several are inserted as a row with the given clear spacing.',
    noOpenings: 'No openings in this wall yet.',
    openingType: 'Opening type',
    gap: 'Spacing',
    gapAria: 'Spacing between the openings',
    insert: 'Insert',
    sill: 'Sill',
    spread: 'Distribution',
    spreadGap: 'Fixed spacing, centred',
    spreadEven: 'Evenly across the wall',
    storeys: 'On storeys',
    storeysAria: 'Storeys the openings are inserted on',
    tooMany: '{n} × {width} do not fit on the wall ({length}).',
    noStorey: 'Please choose at least one storey.',
    floor: 'Storey',
    floorGround: 'Floor (ground floor)',
    floorTooHigh: 'On this storey the opening no longer fits into the wall height.',
    insertAtClick: 'Position: {offset} from wall start (clicked spot)',
    insertCentered: 'Position: centred – click the wall to choose the spot',
  },
  opening: {
    levelAria: 'Floor of the opening',
    dimsPosition: 'Dimensions & position',
    dimsInfo:
      'In the floor plan or in 3D, the opening can be dragged along the wall, even past other openings; released on another one, it jumps to the next free spot.',
    sill: 'Sill height (above floor)',
    head: 'Head height (above floor)',
    heights: 'Sill {sill} · head {head}',
    heightsOver: 'Sill {sill} · head {head} (above {floor})',
    overFloor: 'Measured from the floor of {floor}.',
    swing: 'Opening direction',
    swingInfo:
      'Where the door leaf swings to; the arc in the plan and the door opening while walking follow this setting.',
    flipSide: 'To the other side of the wall',
    flipHinge: 'Swap hinge side',
    slideTo: 'Sliding direction',
    slideInfo:
      'The leaf hangs on a running rail above the opening and slides aside over the wall when walking through. Left and right as seen from outside.',
    slideLeft: '← to the left',
    slideRight: 'to the right →',
    otherFace: 'Other side of the wall',
    mountSide: 'Mounting side',
    mountInfo:
      'Hood with the coil shaft and the side guides sit on one face of the wall; the leaf is a grey curtain with a vision strip (1.40–2.00 m) that rolls up quickly when walking through. Inside is the hall side, otherwise the left face seen from the wall start.',
    mountInside: 'Inside',
    mountOutside: 'Outside',
    slideNoRoom:
      'Not enough wall on this side for the open leaf (needed: {length}). Change the sliding direction or move the gate.',
    colors: 'Colours',
    colorsInfo:
      'Own colours of this opening; without them the trim and gate colour of the building apply. With several openings selected the colour applies to all.',
    frameColor: 'Frame colour',
    leafColor: 'Leaf colour (door or gate leaf)',
    likeBuilding: 'Like building',
    voidPosts: 'Columns at the edges (at most 6 m apart)',
    offsetStart: 'Distance from wall start',
    offsetEnd: 'Distance to wall end',
    toWall: 'Go to wall ({length})',
    leveler: 'Dock leveller (inside the hall)',
    shared:
      'This gate shares the depressed approach with its neighbours; it is set at the first gate of the row.',
    toLead: 'Go to the shared approach',
    gates: 'Docks in this approach',
    addGate: 'Add dock',
    removeGate: 'Remove dock',
    railingLeft: 'Railing left',
    railingRight: 'Railing right',
    railingInfo:
      'Left and right as seen from the yard onto the gates. Next to a hall wall the railing can be left out.',
    approach: 'Dock approach (depressed)',
    approachInfo:
      'Recommendation: 16 m level standing area so the trailer stands level, then a ramp with at most 10 % gradient. The gradient determines the ramp length.',
    pitDepth: 'Depth',
    bayWidth: 'Approach width',
    bayLength: 'Standing area (length)',
    rampLength: 'Ramp (length)',
    slope: 'Ramp gradient (%)',
    slopeAria: 'Ramp gradient',
    approachOffset: 'Approach offset (along the wall)',
    incline: 'Incline',
    vertical: '– (vertical step)',
    totalLength: 'Total length outside',
  },
  sweep: {
    title: 'Swept path',
    info: 'Design vehicle after RASt 06 / EAR. Coloured: area the vehicle covers driving exactly on the guide line; dashed: 0.5 m safety margin (driving tolerance, mirrors); lines: tracks of the outer front wheel and the inner rear wheel. Dark: driver’s cab, arrows: direction of travel. “Add backing up” makes a manoeuvre, e.g. forwards past a dock and then backing into it. Red: driving area off the site or inside a building, orange: only the safety margin. The path blocks nothing and can be moved, turned, locked and hidden like an object; its start stays put when changed.',
    angleLess: '{step}° less',
    angleMore: '{step}° more',
    move: 'Move {n}',
    removeMove: 'Remove',
    addReverse: '+ Add backing up',
    addForward: '+ Add driving forwards',
    addMoveHint: 'Another move starting where the vehicle stops (manoeuvring).',
    conflictSite: 'Driving area leaves the site.',
    conflictSiteMargin: 'Safety margin reaches over the site boundary.',
    conflictBuilding: 'Driving area runs into {name}.',
    conflictBuildingMargin: 'Safety margin reaches {name}.',
    direction: 'Direction',
    forward: 'Forwards',
    reverse: 'Backwards (into a dock)',
    side: 'Turn',
    left: 'Left',
    right: 'Right',
    angle: 'Turning angle',
    uTurn: '180° (U-turn)',
    angleFree: 'Free angle (°)',
    radius: 'Radius (from {min})',
    lead: 'Straight before',
    tail: 'Straight after',
    leadReverse: 'Straight back before the curve',
    tailReverse: 'Straight back to the dock',
    vehicle: 'Vehicle',
    vehicleSize: '{length} × {width}',
    space: 'Space needed (with margin)',
  },
  object: {
    model: '3D model',
    hidden: 'This object is hidden.',
    levelAria: 'Floor of the object',
    heightAboveFloor: 'Height above floor level',
    heightAboveGround: 'Height above ground',
    snapping: 'Snap and dock',
    snappingHint:
      'Off: the object moves freely, without grid and without jumping onto walls or objects. On a computer, holding Alt does this for any object.',
    size: 'Size',
    proportional: 'Scale proportionally',
    rackPlaces: 'Pallet places',
    rackLayout: {
      one: '{n} bay × {levels} levels × {perBay} pallets',
      other: '{n} bays × {levels} levels × {perBay} pallets',
    },
    rackPlacesLow: '{places} ({low} of them for 0.80 m)',
    rackLevels: 'Compartment heights (clear, from the bottom)',
    rackCompartment: 'Compartment {n} · {load}',
    rackTop: 'Compartment {n} top · {load}',
    rackTopClear: '{clear} up to the top of the frame',
    rackLoadHigh: 'pallet 1.60 m',
    rackLoadLow: 'pallet 0.80 m',
    rackLoadNone: 'empty',
    rackResetLevels: 'Standard compartment heights (1.75 m)',
    rackPassages: 'Walk-throughs',
    rackPassageInfo:
      'A bay as a walk-through: up to the passage height it has no beams and pallets, the rack goes on above. A protective mesh lies under the first beam. When walking one can pass through.',
    rackPassageBay: 'Bay {n} as a walk-through',
    rackPassageHeight: 'Passage height',
    rackPassageFree: 'Free (width × height)',
    rackPassageNarrow:
      'The bay is only {width} wide – for a 2.00 m walk-through choose a rack with longer beams.',
    shelfPlaces: 'Places (carton 30 × 60 cm)',
    shelfArea: 'Shelf area',
    shelfLayout: {
      one: '{n} bay × {levels} compartments · {places} places',
      other: '{n} bays × {levels} compartments · {places} places',
    },
    shelfCartons: '3 cartons',
    shelfResetLevels: 'Spread shelves evenly',
    shelfHint:
      'The length snaps to whole bays (1.05 m, each further bay 1.00 m), the height to steps of 50 cm. A compartment counts when a 30 × 60 × 28 cm carton fits; the remaining height is spread evenly over further compartments.',
    rackHint:
      'The length snaps to whole bays; neighbouring bays share one upright frame. From 1.60 m of clear height a 1.60 m pallet is stored, from 0.80 m a 0.80 m pallet, otherwise the compartment stays empty.',
    clearance: 'Clearance',
    clearanceInfo:
      'Operating and maintenance clearance around the object; taken into account by the collision check. Planning note, not a safety assessment.',
    clearanceToggle: 'Operating/maintenance clearance',
    front: 'Front',
    back: 'Back',
    left: 'Left',
    right: 'Right',
    nativeSize: 'Original size',
    scale: 'Scale',
    loadError: 'The model could not be loaded – a placeholder is shown.',
    restoreSize: 'Restore original size',
  },
  multi: {
    title: 'Temporary group',
    summary:
      '{items} selected. Dragging moves them all together; the move tool shows a shared handle.',
    deselect: 'Clear selection',
    deselectHint: 'Also with Esc or a right click on empty space.',
    rotateTogether: 'Rotate together (around the centre of the selection)',
    alignCount: 'Align ({items})',
    alignAria: 'Align',
    align: {
      left: 'Align left',
      'center-x': 'Centre horizontally',
      right: 'Align right',
      top: 'Align top',
      'center-y': 'Centre vertically',
      bottom: 'Align bottom',
    },
    distribute: 'Distribute',
    distributeX: 'Evenly horizontal',
    distributeY: 'Evenly vertical',
    gapAria: 'Fixed spacing (m)',
    gapX: '↔ Spacing',
    gapY: '↕ Spacing',
    gapHint:
      '“Spacing” lines the objects up with exactly this clear distance, e.g. for rack aisles.',
    alongWall: 'Move along the wall ({items})',
    alongAria: 'Shift along the wall (m)',
    towardsStart: '← Towards wall start',
    towardsEnd: 'Towards wall end →',
    spreadOnWall: 'Spread evenly on the wall',
    slideBlocked: 'Another opening is in the way there.',
    sill: 'Sill height ({items})',
    liftAria: 'Change in height (m)',
    raise: '↑ Higher',
    lower: '↓ Lower',
    storeyAbove: 'Copy to the storey above',
    storeysAll: 'Copy to all storeys above',
    storeyHint: 'Ground-floor doors stay downstairs.',
    copied: 'Copied ✓',
    pasteHint:
      'Copy, then click the target wall (e.g. on the upper floor or at the back) and press Ctrl+V – the arrangement stays the same.',
    placement: {
      'no-wall': 'Click the target wall first, then paste.',
      'no-room': 'There is no room for them on this wall.',
      'too-tall': 'The openings are taller than this wall.',
      overlap: 'Other openings are already there.',
    },
  },
  outdoorPanels: {
    points: 'Points ({n})',
    insertPoint: 'Insert point after this one',
    fireLane: 'Fire brigade access roads need a clear width of at least 3 m (DIN 14090).',
    extendRoad: 'To extend, start at the end of the road in the “Road” drawing mode.',
    carPark: 'Car park',
    truckStalls: 'Truck parking spaces',
    perRow: 'Spaces per row',
    rows: 'Rows',
    rows1: '1 (aisle in front)',
    rows2: '2 (aisle in between)',
    stallWidth: 'Space width',
    stallDepth: 'Space depth',
    aisle: 'Aisle',
    roofU: 'Position across (u)',
    roofV: 'Position along (v)',
    roofHint:
      'Position relative to the hall centre. Drag it in the floor plan, resize it at its sides with "Resize". Turns freely on a flat roof, in 90° steps on pitched roofs.',
    toHall: 'Go to hall ({name})',
  },
  zoneRoom: {
    alignToBuilding: 'Align to building',
    editVertices: 'Edit corners',
    floorColor: 'Floor colour',
    roomArea: 'Room area',
    walls: 'Walls',
    sharedWalls: {
      one: 'One side uses the wall of the neighbouring room or of the building.',
      other: '{n} sides use the wall of the neighbouring room or of the building.',
    },
    recalcWalls: 'Recalculate walls',
  },
  measurementPanel: {
    title: 'Saved dimension',
  },
  polygon: {
    edge: 'Edge P{a}–P{b}',
    insertTitle: 'Insert point on this edge',
    add: '+ Point',
    remove: '− Point',
    deleteTitle: 'Delete point',
  },
  background: {
    title: 'Background image',
    empty: 'Site plan or aerial photo',
    tools: { scale: 'Scale', align: 'Point to point', move: 'Move' },
    toolHints: {
      scale: 'Click two points of a known distance in the image',
      align: 'Click an image point, then the target point in the plan',
      move: 'Drag the image in the plan',
    },
    uploading: 'Uploading …',
    upload: 'Upload image or PDF (JPG/PNG/PDF)',
    hint: 'E.g. a screenshot from a geoportal, a scanned site plan or a PDF drawing (stays sharp when zooming, white turns transparent). Then click two points of a known distance to set the scale.',
    levelAria: 'Floor whose plan is edited',
    hintLevel:
      'Plan for the {level}, e.g. the PDF drawing of the upper floor. It shows while the plan shows this floor; then “Take scale and position from the ground floor” or calibrate it anew.',
    onTop: 'Show over the planning',
    onTopHint:
      'The plan lies over halls, offices and rooms so you can trace it; clicks, drawing and snapping pass through. PDFs have transparent paper.',
    fromGround: 'Take scale and position from the ground floor',
    fromGroundHint:
      'Lays this plan exactly over the ground floor plan (same middle, turn and scale) – for plans of the same building on sheets of the same size.',
    pdfPage: '“{name}” has {n} pages – which one is the background?',
    pdfPageOption: 'Page {n}',
    pdfInsert: 'Insert page',
    show: 'Show in plan',
    opacity: 'Opacity',
    opacityValue: 'Opacity {pct} %',
    size: 'Size',
    align: 'Align',
    alignInfo:
      'Scale: click two points of a known distance in the image. Point to point: first an image point, then the target point in the plan. Move: drag the image in the plan. Then lock it.',
    alignAria: 'Align background',
    other: 'Other image …',
    remove: 'Remove',
    takeOver: 'Take over site and background from a project file …',
    takeOverHint:
      'From an exported .indusketch file (e.g. a template with a well-drawn site plan). Replaces the sites and the ground-floor background; buildings and everything else stay.',
    takeOverFrom: 'Take over from "{name}"?',
    takeOverSites: {
      one: '• {n} site – replaces the current sites',
      other: '• {n} sites – replace the current sites',
    },
    takeOverImage: '• the background image – replaces the current ground-floor one',
    takeOverKeeps:
      'Buildings, interior, objects and outdoor facilities stay unchanged. Afterwards move sites and image together under the building; undo with Ctrl+Z.',
    takeOverEmpty: 'The file contains neither sites nor a background image.',
    takeOverMissing:
      'The background image is missing from the file – please export the template as an .indusketch package.',
  },
  videos: {
    title: 'Videos',
    none: 'None',
    count: { one: '{n} video', other: '{n} videos' },
    empty: 'No videos yet. Record a walk-through in 3D with “Record” while walking.',
    play: 'Play',
    download: 'Download',
    delete: 'Delete',
    confirmDelete: 'Delete video “{name}” for good?',
    nameAria: 'Name of the video',
    meta: '{date} · {duration} · {size}',
    by: 'by {name}',
    withSound: 'with sound',
    quota: '{used} of {quota} used',
    upload: 'Upload video file …',
    uploadHint: 'MP4 or WebM, at most {max}',
    loadFailed: 'Videos could not be loaded: {reason}',
  },
  project: {
    title: 'Project settings',
    project: 'Project',
    description: 'Description',
    grid: 'Grid & snapping',
    snapOn: 'snap on',
    gridSize: 'Grid size',
    customGrid: 'Custom grid size',
    showGrid: 'Show grid',
    snap: 'Snap to grid',
    collisions: 'Check collisions',
    alignToBuilding: 'Align to building',
    alignToBuildingHint:
      'In skewed buildings the grid and snapping run along the walls; new rooms, zones, walls and objects stand parallel to the building.',
    display: 'Display',
    displaySummary: '{unit} · roof {roof}',
    unit: 'Unit',
    roof3d: 'Roof in 3D',
    roofAria: 'Roof display',
    roofModes: { visible: 'Visible', transparent: 'Transparent', hidden: 'Off' },
    wallsTransparent: 'Transparent walls',
    structureVisible: 'Show the steel frames of the halls',
    structureRoof: 'Show the roof structure (rafters, purlins, roof bracing)',
    loads: 'Loads (pre-sizing)',
    loadsInfo:
      'Snow and wind zones to EN 1991-1-3 and EN 1991-1-4 with the national annex of the country (Germany: zone maps e.g. from the DIBt; Poland: PN-EN with snow zones 1–5 and wind zones 1–3) and the altitude of the site. Frames, binders, purlins, girts, columns and beams are pre-sized for them. Without a zone 0.85 kN/m² snow on the roof and 0.65 kN/m² wind pressure apply.',
    loadsSummary: '{country} · zone {zone} · {altitude} m above sea level',
    country: 'Country (load codes)',
    countries: { de: 'Germany', pl: 'Poland' },
    windZone: 'Wind zone',
    windZoneDefault: { de: 'Usual (wind zone 2)', pl: 'Usual (zone 1)' },
    terrain: 'Terrain',
    terrainDefault: { de: 'Usual (inland)', pl: 'Usual (category III)' },
    terrains: {
      inland: 'Inland',
      coast: 'Coast and islands',
      open: 'Category II – open country',
      suburban: 'Category III – suburban, industrial',
      urban: 'Category IV – urban',
    },
    windPressure: 'Wind pressure qₚ up to 10 m',
    snowZone: 'Snow load zone',
    snowZoneNone: 'Not given (usual snow load)',
    snowZoneLabel: 'Zone {zone}',
    altitude: 'Altitude (m above sea level)',
    groundSnow: 'Snow on the ground (sₖ)',
    roofLoad: 'Roof load (snow + own weight)',
    loadValue: '{value} kN/m²',
    shortcuts: 'Keyboard shortcuts',
  },
  props: {
    aria: 'Properties',
    back: 'Project settings',
    backTitle: 'Back to the project settings',
    resizeStop: 'Finish resizing (E)',
    shell: 'Protect shell',
    shellHint:
      'Position, size and outline are protected as well – even against “Unlock everything”. Interior, windows, doors and gates stay editable.',
    shellConfirm: 'Really release the shell of “{name}”? It can then be moved and changed again.',
    resizeStart: 'Resize (E)',
    moveStart: 'Move (S)',
    resizeTitle: 'Drag the handles at the ends or sides – key E switches them on and off',
    moveTitle: 'Drag the move handle on the element – key S',
  },
  rotation: {
    by: 'Rotate by',
    angle: 'Rotation',
    ccw90: '90° anticlockwise',
    straighten: 'Straighten (square to the hall)',
    cwFine: '0.5° clockwise (arrow down)',
    ccwFine: '0.5° anticlockwise (arrow up)',
    cw90: '90° clockwise',
    steps: '{label}: 90° and 0.5° steps',
  },
  lock: {
    label: '🔒 Lock',
    hint: 'Locked against moving, rotating and dragging. Values here remain editable.',
  },
  info: {
    about: 'Note on {title}',
    plain: 'Note',
    show: 'Show note',
  },
  outdoor: {
    drawTitle: 'Draw in the plan',
    drawInfo:
      'Roads, car parks and fences can be moved in the plan; adjust width, type and points on the right. Starting at the end of a road or fence extends it.',
    drawRoad: 'Draw road / path',
    drawParking: 'Draw car park',
    drawFence: 'Draw fence',
    surfacesTitle: 'Surfaces and green areas',
    surfacesInfo:
      'Choose the type, then click the corners in the plan (e.g. on the site plan) or quickly drag a rectangle. The areas appear in the area overview and in the PDF under “Outdoor areas”.',
    parkingTitle: 'Car park by quantity',
    stalls: 'Parking spaces',
    car: 'Car',
    truck: 'Truck',
    countAria: 'Number of parking spaces',
    invalidCount: 'Please enter 1 to 400 parking spaces.',
    insertParking: 'Insert car park',
    fenceTitle: 'Fence',
    fenceInfo:
      'Follows the outer boundary of all sites; boundaries between your own plots stay open.',
    fenceAlongSite: 'Fence along the site boundary',
  },
  dock: {
    title: 'Truck loading dock',
    info: 'Gate with a depressed approach: 16 m level standing area, 10 % ramp, 1.20 m deep, dock leveller 2 × 3 m. Several docks stand 4 m apart (centre to centre) in one shared approach, with railings only on the far left and far right.',
    single: 'Single dock',
    double: 'Double dock',
    count: 'Number of docks',
    row: '{n} docks side by side',
    place: 'Place on a hall wall',
    needsHall: 'Create a hall first – the dock sits in a hall wall.',
    placeHint: 'Now tap the hall wall where the dock should be.',
  },
  openings: {
    placeHint: 'Tap the wall in the plan or in 3D – as many as you like, one after another.',
  },
  library: {
    search: 'Search objects …',
    searchAria: 'Search objects',
    placeHint: 'Tap in the plan or the 3D view to place it. R rotates, Shift+click places more.',
    nothingFound: 'Nothing found.',
    sizeRefs: 'Size references',
    sizeRef: 'Size reference {name}',
    ownModels: 'My 3D models',
    uploading: 'Uploading and checking …',
    addGlb: 'Add GLB model',
    dropHint: 'Drag a file here or choose one · Draco/Meshopt supported',
    system: 'System library',
    heightShort: 'H',
  },
  placed: {
    none: 'No objects placed yet.',
    hidden: '{n} hidden',
    showAll: 'Show all',
    show: 'Show {label}',
    hide: 'Hide {label}',
    countHidden: '{n} ({hidden} off)',
    focus: 'Select and zoom to it',
  },
  measure: {
    save: 'Save as dimension',
    hint: 'Drag from point to point in the plan. Corners, edges and walls are snapped.',
  },
  selectOptions: {
    multi: 'Multiple selection (tap adds)',
    multiTitle: 'Tapping adds to or removes from the selection',
    distances: 'Distances to the selected object',
    distancesTitle: 'Clearances between the selected object and its surroundings',
  },
  keys: {
    paste: 'Ctrl+V',
    copy: 'Ctrl+C',
    cut: 'Ctrl+X',
    duplicate: 'Ctrl+D',
    selectAll: 'Ctrl+A',
    rotate: 'R',
    rotateBack: 'Shift+R',
    zoomSelection: 'F',
    move: 'S',
    resize: 'E',
    delete: 'Del',
  },
  shortcuts: {
    undo: { keys: 'Ctrl+Z / Ctrl+Y', text: 'Undo / Redo' },
    save: { keys: 'Ctrl+S', text: 'Save' },
    clipboard: { keys: 'Ctrl+C / X / V', text: 'Copy / Cut / Paste' },
    duplicate: { keys: 'Ctrl+D', text: 'Duplicate' },
    selectAll: { keys: 'Ctrl+A', text: 'Select all objects' },
    delete: { keys: 'Del', text: 'Delete selection' },
    nudge: { keys: 'Arrow keys', text: 'Move selection by one grid step (Shift: ×10)' },
    rotate: { keys: 'R / Shift+R', text: 'Rotate object by 90° (also the object at the pointer)' },
    placeMore: {
      keys: 'Shift+click while placing',
      text: 'Place the object and start placing another one',
    },
    tools: { keys: 'V · W · S · R · M', text: 'Tool Select · Draw · Move · Rotate · Measure' },
    resize: { keys: 'E', text: 'Resize: handles on the selected element on/off' },
    multi: {
      keys: 'Shift+click / Shift+drag',
      text: 'Multiple selection / box selection – a right click on empty space or Esc clears it',
    },
    zoomSelection: { keys: 'F', text: 'Zoom to selection (2D plan)' },
    grid: { keys: 'G', text: 'Show/hide grid' },
    zoom: { keys: '+ / −', text: 'Zoom in / out in the 2D plan' },
    pan: {
      keys: 'Space + drag',
      text: 'Move the plan or 3D view – in every tool, even in the middle of drawing',
    },
    siteDraft: {
      keys: 'Enter / Backspace',
      text: 'Drawing point by point (site, area, road, fence): finish / remove last point',
    },
    escape: {
      keys: 'Esc',
      text: 'Discard the site being drawn, finish resizing, then cancel selection, measuring and placing',
    },
  },
  drive: {
    getIn: 'Get in (E)',
    getOut: 'Get out (E)',
    forkUp: 'Forks ▲ (R)',
    forkDown: 'Forks ▼ (F)',
    drop: 'Put down (G)',
    view: 'View (V)',
    hint: 'W/S: throttle, brake, reverse · A/D: steer',
    hintForks:
      'W/S: drive · A/D: steer · forks under the pallet, then R to lift · lower fully or G to put down',
  },
  vr: {
    button: 'VR',
    buttonTitle: 'Walk through with the VR headset',
    suggestTitle: 'VR headset detected',
    suggestText: 'Explore buildings and sites in VR.',
    suggestGame: 'Join the safety game with the VR headset.',
    projectsHint: 'Open a project to explore its buildings and sites in VR.',
    enter: 'Start VR',
    later: 'Later',
    menuTitle: 'VR menu',
    help: 'Left stick: walk (push fully: run) · Right stick: turn · Y or B: menu',
    toStart: 'Back to the start',
    turnSnap: 'Turning: in steps (30°)',
    turnSmooth: 'Turning: smooth',
    startGame: 'Start safety game',
    endGame: 'End safety game',
    exit: 'Leave VR',
    close: 'Close menu',
  },
  context: {
    pasteHere: 'Paste here',
    selectAll: 'Select all objects',
    selectEverything: 'Select everything',
    lockEverything: 'Lock everything',
    unlockEverything: 'Unlock everything',
    protectShells: 'Protect all building shells and sites',
    deselect: 'Clear selection',
    rotateLeft90: 'Rotate 90° left',
    straighten: 'Straighten',
    rotateRight90: 'Rotate 90° right',
    rotateLeftFine: 'Rotate 0.5° left',
    rotateRightFine: 'Rotate 0.5° right',
    selectAllSites: 'Select all sites',
    cut: 'Cut',
    duplicateOpening: 'Duplicate (next free spot)',
    unlock: 'Unlock',
    lock: 'Lock (against moving)',
    resize: 'Resize',
    move: 'Move',
    insertVertex: 'Insert corner here',
    railingEdgeOff: 'Remove the railing on this edge',
    railingEdgeOn: 'Put the railing back on this edge',
    removeVertex: 'Delete nearest corner (P{n})',
    zoomSelection: 'Zoom to selection',
    attachSite: 'Attach site …',
  },
  plan: {
    aria: '2D floor plan',
    zoom: 'Zoom',
    zoomIn: 'Zoom in (+)',
    zoomOut: 'Zoom out (−)',
    zoomSelection: 'Zoom to selection (F)',
    grid: 'Show/hide grid (G)',
    dimsToggle: 'Show/hide dimensions and labels',
    dimsMenu: 'Dimensions and labels',
    dims: 'Dimensions',
    labels: 'Labels',
    dimItems: {
      site: 'Site edges',
      building: 'Hall dimensions',
      measurements: 'Saved dimensions',
      grid: 'Column grid and bays',
      distances: 'Distances (object)',
    },
    labelItems: {
      site: 'Sites (name, m²)',
      building: 'Halls and office buildings',
      mezzanines: 'Mezzanines (name, m²)',
      interior: 'Zones and rooms',
      areas: 'Areas (name, m²)',
      outdoor: 'Roads and parking spaces',
      objects: 'Object names',
      symbols: 'Loading docks and stairs',
    },
    tapWall: 'Please tap directly on a wall to insert the opening.',
    stallsPreview: {
      one: '{n} parking space ({rows} × {perRow})',
      other: '{n} parking spaces ({rows} × {perRow})',
    },
    tooSmall: 'Too small for a parking space',
    north: 'North is up',
    closeHere: 'Click here to close',
    snap: { corner: 'Corner', midpoint: 'Midpoint', edge: 'Edge', wall: 'Wall', grid: 'Grid' },
    siteTooFew: 'A site needs at least three corners.',
    siteSelfIntersect: 'The site boundary must not cross itself.',
    areaTooFew: 'An area needs at least three corners.',
    areaSelfIntersect: 'The outline must not cross itself.',
    mezzanineNoHall: 'A mezzanine has to be drawn inside a hall.',
    lineTooShort: 'The line is too short.',
  },
  planBackground: {
    scale1: 'Click the first point of a known distance in the image',
    scale2: 'Click the second point of the distance',
    align1: 'Click a point in the image (e.g. a site corner)',
    align2: 'Click the target point in the plan – snaps to corners',
    move1: 'Drag the image with the mouse button or finger held down',
    calibrate: 'Distance in the image currently {length}. Actual length:',
    realLength: 'Actual length',
    example: 'e.g. 48.5',
    apply: 'Apply',
  },
  resizeLabels: {
    edges: 'Edges {a} / {b}',
    edge: 'Edge {length} · offset {shift}',
    length: 'Length {length}',
    width: 'Width {length}',
    stalls: { one: '{n} parking space · {length}', other: '{n} parking spaces · {length}' },
  },
  dashboard: {
    aria: 'Area overview',
    title: 'Areas',
    headlineBuilt: 'Built {built} · hall free {free}',
    headlineSite: 'Site {area}',
    stillFree: 'still free',
    mezzanine: 'Mezzanine {name}',
    storeyArea: 'Total floor area',
    totalStoreyArea: 'Total floor area (all levels)',
    officeMode: 'Office mode',
    daylight: 'Roof daylight',
    traffic: 'Traffic areas',
    roads: 'Roads / paths',
    parking: 'Car parks',
    carStalls: 'Car parking spaces',
    fenceLength: 'Fence length',
    area: 'Area {n}',
    palletPlaces: 'Pallet places',
    palletPlacesLow: 'of which for 0.80 m pallets',
    palletRacks: { one: 'in {n} pallet rack', other: 'in {n} pallet racks' },
    shelfPlaces: 'Shelf places',
    shelfArea: 'Shelf area',
    shelfRacks: { one: 'in {n} shelving unit', other: 'in {n} shelving units' },
    outdoorZones: 'Outdoor areas',
    built: 'Built-up area',
    freeSite: 'Free site area',
    coverage: 'Site coverage',
    more: '… and {n} more notes',
    warnings: { one: '{n} note', other: '{n} notes' },
    warningsToggle: 'Show or hide notes',
  },
  status: {
    mode: 'Mode: {tool}',
    grid: 'Grid: {size}',
    snap: 'Snap: {state}',
    on: 'on',
    off: 'off',
  },
  officeMode: {
    aria: 'Office mode',
    title: 'Office mode · {name}',
    exit: 'Exit office mode',
    slabsAria: 'Show/hide slabs',
    showSlab: 'Show slab',
    hideSlab: 'Hide slab',
    slabAbove: 'Slab above {level}',
  },
  scene: {
    aria: '3D display',
    sectionTitle: 'Section through a floor: everything above is hidden',
    sectionAria: 'Floor section',
    allLevels: 'All floors',
    sectionAt: 'Section {level}',
    slabs: 'Slabs',
    grid: 'Grid',
    shadows: 'Shadows',
    shadowsTitle: 'Soft shadows (costs performance on tablets)',
    showRoof: 'Show roof',
    hideRoof: 'Hide roof',
    structure: 'Steel frame',
    structureTitle: 'Show or hide the columns and rafters of the halls',
    structureRoof: 'Roof structure',
    structureRoofTitle:
      'Show or hide rafters, purlins, roof bracing and haunches; columns and girts stay',
    screenshot: 'Screenshot',
    options: 'View',
    walk: 'Walk',
    walkTitle: 'Walk through the site as a person',
    walkStop: 'Stop walking',
    walkPlace: 'Click into the scene where you want to start.',
    walkHint:
      '↑ ↓: walk · ← →: turn · Shift: run mode on/off · V: view · hold B: look back · drag mouse: look around · click: select · drag an object: move it · Esc: stop',
    walkHintTouch:
      'Joystick: forwards/back walks, left/right turns · swipe: look around · tap: select · drag an object: move it',
    walkJoystick: 'Walk',
    walkRun: 'Run',
    walkRunTitle: 'Run mode on/off (key: Shift); only with it on do you run',
    walkViews: { eye: 'Own eyes', shoulder: 'Shoulder', behind: 'Behind' },
    walkViewTitle: 'Switch the view: own eyes, over the shoulder, from behind (key: V)',
    walkBack: 'Back',
    walkBackTitle: 'Hold to look back (key: B)',
    record: 'Record',
    recordTitle: 'Record the walk-through as a video (downloaded when you stop)',
    recordStop: 'Stop recording',
    recording: 'Recording',
    recordSaved: 'Video saved: {file}',
    walkVideoLabel: 'Walkthrough',
    microphone: 'Microphone',
    microphoneTitle:
      'Record your own commentary through the microphone into the video (from the next recording)',
    micUnavailable: 'No microphone available or not allowed – the video is recorded without sound.',
    videoQualityHigh: 'High',
    videoQualityStandard: 'Standard',
    videoQualityTitle:
      'Video quality: standard (720p, about 22 MB per minute) or high (Full HD, about 60 MB per minute); from the next recording',
    recordFailed: 'The recording failed. Please try again, if need be in standard quality.',
    videoUploaded: 'Video saved in the project: {file}',
    videoUploadFailed:
      'The video could not be saved in the project ({reason}). The file was downloaded and can be uploaded later under “Videos”.',
    videoUploading: 'Saving video in the project … {percent} %',
  },
  spaceMouse: {
    connectedAria: 'SpaceMouse connected: {device}',
    connect: 'Connect SpaceMouse',
    connected: 'Connected: {device}',
    notConnected: 'Not connected',
    ratesTitle:
      'Few signals: device/driver. Few frames: the computer draws the 3D scene too slowly.',
    rates: 'Diagnostics: {reports} signals/s · {frames} frames/s',
    speed: 'Speed {pct} %',
    invertPan: 'Invert panning',
    invertZoom: 'Invert zoom',
    invertRotate: 'Invert rotation',
    help: '2D: pushing the cap pans, pressing/pulling zooms. 3D: pushing pans, forward/back zooms, tilting and twisting rotates the view. Buttons: Menu switches 2D/3D, Fit shows everything, T/F/R choose Top/Front/Side. Chrome and Edge only.',
    busy: 'The SpaceMouse is in use by another program or tab.',
    failed: 'Connection not possible.',
  },
  screenshot: {
    title: 'Screenshot of the 3D view',
    hint: 'The current viewpoint is captured – without toolbars, selection and handles.',
    resolution: 'Resolution',
    a4: 'A4 landscape, 300 dpi (3508 × 2480)',
    background: 'Background',
    backgrounds: { white: 'White', transparent: 'Transparent', scene: 'As in the view' },
    creating: 'Creating …',
    save: 'Save image',
  },
  report: {
    title: 'PDF design report',
    intro:
      'The report contains project data, key figures, the scaled floor plan with legend, two 3D perspectives and lists of areas, gates and objects. It is created from the saved state; unsaved changes are saved first.',
    includeCosts: 'Include quantities and cost estimate (unit prices from the project settings)',
    omitHidden: {
      one: 'Leave {n} hidden object out of the report',
      other: 'Leave {n} hidden objects out of the report',
    },
    steps: {
      saving: 'Saving the current state …',
      rendering: 'Rendering 3D views',
      generating: 'Creating the PDF …',
    },
    stepOf: '{i} of {n}',
    language: 'Report language',
    done: 'The report has been downloaded.',
    create: 'Create PDF',
  },
  messages: {
    commandFailed: '“{label}” failed. The plan was not changed.',
    commandRejected: '“{label}” not possible',
    commandRejectedWhy: '“{label}” not possible: {reason}',
    saveConflict: 'The project was changed elsewhere. Nothing was overwritten.',
    resolveConflictFirst: 'The project was changed elsewhere – please resolve the conflict first.',
    saveFailed: 'Saving failed – please check the connection.',
    noProject: 'No project open',
    imageRead: 'The image could not be read',
    glbOnly: 'Please choose a .glb file.',
    tooLarge: 'The file is too large (max. {mb} MB).',
    openFailed: 'The project cannot be opened: {reason}',
    loadingProject: 'Loading project …',
    closeMessage: 'Close message',
  },
};
